// src/components/IdleResetManager.js
// TKT-2026-103353: auto-reset the kiosk to the store home screen after a
// configurable period of inactivity, so it is clean and ready for the next
// customer at shows. Renders nothing.
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useKioskTheme } from '../contexts/KioskThemeContext';

const ACTIVITY_EVENTS = [
    'pointerdown',
    'mousedown',
    'keydown',
    'touchstart',
    'touchmove',
    'wheel',
    'scroll',
    'click',
];

const THROTTLE_MS = 500;

export default function IdleResetManager() {
    const location = useLocation();
    const navigate = useNavigate();
    const { requestReset } = useCart();
    const { themeOverrides } = useKioskTheme();

    const timerRef = useRef(null);
    const lastActivityRef = useRef(0);

    // First non-empty path segment is the store slug (matches cartStorage.deriveStorageKey).
    const slug = (location.pathname || '').split('/').filter(Boolean)[0] || '';

    const enabled = themeOverrides?.idleResetEnabled === true;
    const minutes = Number(themeOverrides?.idleResetTimeoutMinutes) || 5;
    const active = enabled && !!slug && slug !== 'select-store';

    useEffect(() => {
        if (!active) {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
            return undefined;
        }

        const timeoutMs = Math.max(1, minutes) * 60 * 1000;

        const fire = () => {
            // requestReset() clears the cart and returns false if a checkout/collection
            // code is currently on screen, in which case we simply re-arm.
            if (requestReset()) {
                navigate(`/${slug}`);
            } else {
                arm();
            }
        };

        const arm = () => {
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(fire, timeoutMs);
        };

        const onActivity = () => {
            const now = Date.now();
            if (now - lastActivityRef.current < THROTTLE_MS) return;
            lastActivityRef.current = now;
            arm();
        };

        arm();
        ACTIVITY_EVENTS.forEach((evt) =>
            window.addEventListener(evt, onActivity, { passive: true, capture: true })
        );

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
            ACTIVITY_EVENTS.forEach((evt) =>
                window.removeEventListener(evt, onActivity, { capture: true })
            );
        };
    }, [active, minutes, slug, requestReset, navigate]);

    return null;
}
